import { AxiosError } from 'axios'
import { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { usePermissions } from '.'
import workspaceService, {
  GetWorkspaceListParams,
  WorkspaceResponse,
} from '../services/workspaceService'
import { RootState } from '../store'
import {
  Workspace,
  setWorkspaceList,
  setWorkspaceListCsv,
  setWorkspaceListUiFlags,
  setWorkspaceTotalResults,
} from '../store/reducers/workspaceReducer'
import useDownloadFile from './useDownloadFile'

interface UseWorkspaceListProps {
  preventFetch?: boolean
  params?: GetWorkspaceListParams
}

const useWorkspaces = ({
  preventFetch = false,
  params = {},
}: UseWorkspaceListProps) => {
  const dispatch = useDispatch()
  const workspaceList: Workspace[] | undefined = useSelector(
    (state: RootState) => state.workspace.workspaceList
  )
  const totalResults: number | undefined = useSelector(
    (state: RootState) => state.workspace.totalResults
  )
  const downloadCsv = useDownloadFile()
  const [error, setError] = useState<AxiosError | Error>()
  const [loading, setLoading] = useState(false)

  const uiFlag: number | undefined = useSelector(
    (state: RootState) => state.workspace.uiFlags
  )

  const { isDeletable, isUpdatable, isCreatable } = usePermissions({
    uiFlag: uiFlag ?? 0,
  })

  const fetchWorkspaceList = useCallback(
    (refreshForceCall = false) => {
      if (!loading && (!preventFetch || refreshForceCall)) {
        setLoading(true)
        workspaceService
          .getWorkspaces(params)
          .then((response: WorkspaceResponse) => {
            setLoading(false)
            dispatch(setWorkspaceList(response.data))
            dispatch(setWorkspaceTotalResults(response.totalResults))
            dispatch(setWorkspaceListUiFlags(response.uiFlags || 0))
          })
          .catch((error: AxiosError | Error) => {
            setError(error)
            console.error('axios fetch error', error)
            setLoading(false)
          })
      }
    },
    [dispatch, preventFetch, loading, params]
  )

  const refreshWorkspaceList = useCallback(async () => {
    fetchWorkspaceList(true)
  }, [fetchWorkspaceList])

  useEffect(() => {
    fetchWorkspaceList()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.page, params.pageSize, params.sortField, params.sortOrder])

  const exportWorkspaceListCsv = useCallback(() => {
    if (workspaceList) {
      workspaceService
        .exportWorkspaceList()
        .then((response) => {
          const fileName = 'workspace_list.csv'
          downloadCsv(response, fileName)
          dispatch(setWorkspaceListCsv(response))
        })
        .catch((error: any) => {
          console.error('Error fetching workspace list CSV data', error)
        })
    }
  }, [workspaceList, dispatch, downloadCsv])

  return {
    error,
    exportWorkspaceListCsv,
    fetchWorkspaceList,
    loading,
    refreshWorkspaceList,
    workspaceList,
    totalResults,
    isDeletable,
    isUpdatable,
    isCreatable,
  }
}

export default useWorkspaces
